import { Button } from "@/v2-components/ui/button";
import { useTrans } from "@/utils/useTrans";
import { Fragment, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input } from "@/v2-components/ui/input";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/v2-components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/v2-components/ui/select";
import { Label } from "@/v2-components/ui/label";
import { Message } from "@/v2-components/core/message";
import { Spinner } from "@/v2-components/ui/spinner";
import { Product, ProductSlug } from "@/api/product-api";
import { Checkbox } from "@/v2-components/ui/checkbox";
import { SwitchCase } from "@/v2-components/switch-case";
import RetailerSheet from "@/v2-components/pages/tactsuit/retailer-sheet";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import {
  useProductBySlug,
  useProducts,
} from "@/services/product/useProductService";
import useUser from "@/api/apis";
import { useNotifications } from "@/hooks/useNotifications";
import {
  ALL_COLORS_VALUE,
  ALL_SIZES_VALUE,
  getNotifyMeOptions,
  getSoldOutSizesForColor,
  resolveNotificationTargets,
} from "@/utils/notify-me-options";

function buildSchema({
  requireColor,
  requireSize,
}: {
  requireColor: boolean;
  requireSize: boolean;
}) {
  return yup.object().shape({
    email: yup
      .string()
      .email("Please enter a valid email address.")
      .required("Email is required")
      .default("Email Address"),
    color: requireColor
      ? yup.string().required("Please select a color")
      : yup.string().default(""),
    size: requireSize
      ? yup.string().required("Please select a size")
      : yup.string().default(""),
    check: yup
      .boolean()
      .oneOf([true], "You must accept the agreement to receive notifications."),
  });
}
type SchemaType = yup.InferType<ReturnType<typeof buildSchema>>;

interface FormProps {
  productSlug: ProductSlug;
  /** 카탈로그 대신 이 제품으로 선택 칸을 만든다 — `NotifyMe` 의 `productOverride` 참고. */
  productOverride?: Product;
  initialColor?: string;
  initialSize?: string;
  onSubmit: (data: SchemaType) => Promise<void>;
}

function Form({
  productSlug,
  productOverride,
  initialColor,
  initialSize,
  onSubmit,
}: FormProps) {
  const { t } = useTrans(["gnb"]);
  const user = useUser();
  const [isAuto, setIsAuto] = useState(!!user.user);
  const products = useProducts();
  const product =
    productOverride ??
    products.data?.find((product) => product.slug === productSlug);

  const variantConfig = getNotifyMeOptions(product);
  // 사이즈는 컬러 종속 — 선택된 컬러에 따라 sold-out 사이즈 목록이 동적으로 결정됨

  // 모달 첫 오픈 시 size 기본값 — PDP 에서 prefill 받은 게 없으면, 해당 컬러의 첫 OOS 사이즈 자동 선택 (편의)
  const computedInitialSize = (() => {
    if (initialSize) return initialSize;
    if (!initialColor || !product) return "";
    const sizes = getSoldOutSizesForColor(product, initialColor);
    return sizes[0]?.value ?? "";
  })();

  // 스키마는 variant 노출 여부 기반으로 동적 구성 — 모든 에러가 한 번에 표시되도록
  const schema = useMemo(
    () =>
      buildSchema({
        requireColor: variantConfig.hasColorVariant,
        requireSize: variantConfig.hasSizeVariant,
      }),
    [variantConfig.hasColorVariant, variantConfig.hasSizeVariant],
  );

  const {
    setValue,
    clearErrors,
    trigger,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SchemaType>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: user.user ? user.user.email : "",
      color: initialColor ?? "",
      size: computedInitialSize,
      check: false,
    },
  });

  const selectedColor = watch("color");
  const selectedSize = watch("size");
  const dynamicSoldOutSizes = getSoldOutSizesForColor(product, selectedColor);

  const x16 = useProductBySlug(
    ProductSlug.TACTSUIT_X16,
    productSlug === ProductSlug.TACTSUIT_AIR,
  );

  const submit = async (data: SchemaType) => {
    try {
      await onSubmit(data);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Fragment>
      <DialogDescription>
        {t(
          `We'll notify you about ${product?.name ?? productSlug} at your email address:`,
        )}
      </DialogDescription>
      <form onSubmit={handleSubmit(submit)} className="flex flex-col pt-5">
        {!!user.user && isAuto ? (
          <>
            <p className="text-base font-400 text-coolgray-90">
              {user.user.email}
            </p>
            <button
              type="button"
              onClick={() => {
                // 사용자 이메일을 채워서 수정 시작점으로 제공 (빈 값으로 두지 않음)
                setValue("email", user.user?.email ?? "");
                setIsAuto(false);
              }}
              className="mt-1 flex text-base font-400 text-blue-60 underline"
            >
              {t("Change email")}
            </button>
          </>
        ) : (
          <>
            <Label variant="text" className="flex flex-col gap-2">
              {t("Email")}
              <Input
                placeholder={"bhaptics@example.com"}
                {...register("email")}
              />
              {errors.email && <Message error>{errors.email?.message}</Message>}
            </Label>
          </>
        )}

        {variantConfig.hasColorVariant &&
          (variantConfig.soldOutColors.length > 0 ||
            variantConfig.showAllColors) && (
            <div className="flex flex-col gap-2 pt-5">
              <Label variant="text">{t("Select Color")}</Label>
              <Select
                value={selectedColor || undefined}
                onValueChange={(v) => {
                  setValue("color", v);
                  clearErrors("color");
                  // 새 컬러의 OOS 사이즈 목록에 기존 size 가 있으면 유지, 없으면 초기화
                  const nextSizes = getSoldOutSizesForColor(product, v);
                  const stillValid = nextSizes.some(
                    (s) => s.value === selectedSize,
                  );
                  if (!stillValid) setValue("size", "");
                }}
              >
                <SelectTrigger variant="form" className="h-14 text-base">
                  <SelectValue placeholder={t("Select Color")} />
                  <ChevronDownIcon />
                </SelectTrigger>
                <SelectContent className="z-[100] mt-2">
                  {variantConfig.showAllColors && (
                    <SelectItem value={ALL_COLORS_VALUE}>
                      {t("All colors")}
                    </SelectItem>
                  )}
                  {variantConfig.soldOutColors.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {t(c.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.color && <Message error>{errors.color?.message}</Message>}
            </div>
          )}

        {variantConfig.hasSizeVariant && dynamicSoldOutSizes.length > 0 && (
          <div className="flex flex-col gap-2 pt-5">
            <Label variant="text">{t("Select Size")}</Label>
            <Select
              // key 에 selectedColor 를 넣어 컬러 변경 시 Select 재마운트 — value 표시가 정확히 placeholder 로 복귀
              key={selectedColor}
              value={selectedSize || undefined}
              onValueChange={(v) => {
                setValue("size", v);
                clearErrors("size");
              }}
            >
              <SelectTrigger variant="form" className="h-14 text-base">
                <SelectValue placeholder={t("Select Size")} />
                <ChevronDownIcon />
              </SelectTrigger>
              <SelectContent className="z-[100] mt-2">
                {/* 사이즈만 있는 제품이 전부 품절이면 한 번에 — 「All colors」 의 사이즈판(#5922) */}
                {variantConfig.showAllSizes && (
                  <SelectItem value={ALL_SIZES_VALUE}>
                    {t("All sizes")}
                  </SelectItem>
                )}
                {dynamicSoldOutSizes.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {t(s.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.size && <Message error>{errors.size?.message}</Message>}
          </div>
        )}

        <div className="flex flex-col gap-2 pt-5">
          <Label variant="checkbox" className="flex gap-2 text-coolgray-50">
            <Checkbox
              className="border-coolgray-50 data-[state=checked]:bg-coolgray-50 data-[state=indeterminate]:bg-coolgray-50"
              {...register("check")}
              onCheckedChange={(checked) => {
                setValue("check", !!checked);
                trigger("check");
              }}
            />
            <span>{t(`I agree to restock notifications`)}</span>
          </Label>
          {errors.check && <Message error>{errors.check?.message}</Message>}
        </div>
        <DialogFooter className="mt-9 w-full items-center justify-end">
          <Button
            size="sm"
            variant="blue-fill"
            shape="pill"
            className="min-w-[100px]"
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? (
              <Spinner className="absolute scale-50" />
            ) : (
              t("Submit")
            )}
          </Button>
        </DialogFooter>

        {(productSlug === ProductSlug.TACTSUIT_AIR ||
          productSlug === ProductSlug.STARTER_BUNDLE) &&
          x16.data && (
            <RetailerSheet
              retailers={x16.data.retailers ?? []}
              trigger={
                <Button
                  size="sm"
                  variant="blue-text"
                  className="gap-0 self-center pt-6 underline"
                >
                  <span>{t(`Or Find a TactSuit X16 Retailer`)}</span>
                  <ChevronRightIcon className="size-5" />
                </Button>
              }
            />
          )}
      </form>
    </Fragment>
  );
}

function Success() {
  const { t } = useTrans(["gnb"]);
  return (
    <Fragment>
      <DialogDescription>
        {t("We will notify you immediately once the item is back in stock.")}
      </DialogDescription>
      <DialogFooter className="mt-9 w-full items-center justify-end">
        <DialogClose asChild>
          <Button size="sm" variant="blue-fill" shape="pill" className="px-7">
            {t("Done")}
          </Button>
        </DialogClose>
      </DialogFooter>
    </Fragment>
  );
}

export const NotifyMe = ({
  productSlug,
  variant = "yellow-fill",
  title = "Notify me",
  productSku = "",
  initialColor,
  initialSize,
  trigger,
  open: controlledOpen,
  onOpenChange,
  productOverride,
  slugBySku,
  ...rest
}: {
  productSku?: string;
  productSlug: string;
  className?: string;
  title?: string;
  variant?: typeof Button.prototype.variant;
  initialColor?: string;
  initialSize?: string;
  // 외부에서 trigger UI 를 주입할 수 있게 (예: PDP 의 "Notify me" 텍스트 링크)
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * 카탈로그의 `productSlug` 제품 대신 **이 제품의 SKU 옵션**으로 선택 칸·신청 대상을 만든다.
   * 사이즈마다 별도 제품인 사이즈 라인(이너글러브 S/M/L/XL)을 「사이즈 옵션이 있는 한 제품」으로
   * 넘길 때 쓴다(`libs/product/size-line.ts` 의 `sizeLineNotifyProduct`). 없으면 종전대로 카탈로그.
   */
  productOverride?: Product;
  /** SKU → 그 SKU 를 가진 제품 slug. `productOverride` 와 함께 — `resolveNotificationTargets` 참고. */
  slugBySku?: Record<string, string>;
}) => {
  const { t } = useTrans(["gnb"]);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [phase, setPhase] = useState<"form" | "success">("form");
  const products = useProducts();
  const { notifyStock } = useNotifications();

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setPhase("form");
        }
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant={variant} shape="pill" size="sm" {...rest}>
            {t(title)}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="flex min-h-[200px] max-w-[400px] flex-col p-6">
        <DialogHeader>
          <DialogTitle>{t(title)}</DialogTitle>
          <DialogClose />
        </DialogHeader>
        <SwitchCase
          value={phase}
          caseBy={{
            form: (
              <Form
                productSlug={productSlug as ProductSlug}
                initialColor={initialColor}
                initialSize={initialSize}
                productOverride={productOverride}
                onSubmit={async (data) => {
                  // 신청 대상 판정은 `resolveNotificationTargets` 하나 — 변형(색·사이즈) 해석 ·
                  // 품절 SKU 없을 때 폴백 · 행별 slug(사이즈 라인)까지. 종전 인라인 로직과 같은 결과다.
                  const targets = resolveNotificationTargets({
                    product:
                      productOverride ??
                      products.data?.find((p) => p.slug === productSlug),
                    productSlug,
                    productSku,
                    color: data.color,
                    size: data.size,
                    slugBySku,
                  });
                  await Promise.all(
                    targets.map((target) =>
                      notifyStock({
                        email: data.email,
                        productSlug: target.productSlug,
                        sku: target.sku,
                      }),
                    ),
                  );
                  setPhase("success");
                }}
              />
            ),
            success: <Success />,
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
